import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { getQueryExecutionHistory, executeQuery, deleteQueryExecution } from '../services/agentApi';
import { refineNLPQuery, testRefinedQuery, updateNLPQuery } from '../services/api';
import {
    IMPLEMENTATION_STATUS,
    buildReportPlanReturnPath,
    upsertImplementation,
} from '../utils/reportPlanImplementations';
import { PlainQueryDisplay } from '../components/ui/PlainQueryDisplay';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import {
    ArrowLeft,
    Play,
    AlertCircle,
    Loader,
    History,
    CheckCircle,
    Database,
    Sparkles,
    Table,
    Clock,
    TrendingUp,
    Trash2,
    X
} from 'lucide-react';

/**
 * QueryExecutionPage - Dedicated page for executing a query and viewing its history.
 */
export const QueryExecutionPage = () => {
    const { t } = useTranslation(['page-query-execution', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { agentId, queryId } = useParams();
    const { showToast } = useToast();

    // The query object and database ID passed from SubAgentQueries
    const query = location.state?.query;
    const databaseId = location.state?.databaseId;
    const agentName = location.state?.agentName || t('defaultAgentName');

    const [parameters, setParameters] = useState({});
    const [errors, setErrors] = useState({});
    const [isExecuting, setIsExecuting] = useState(false);
    
    // Execution History & latest result
    const [historyLoading, setHistoryLoading] = useState(false);
    const [executions, setExecutions] = useState([]);
    
    // executionData holds the result of the latest run, or a specifically selected history run
    const [executionData, setExecutionData] = useState(null);

    // Confirmation modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        executionId: null,
        title: t('deleteExecutionTitle'),
        message: t('deleteExecutionMessage'),
    });

    // Query refinement state (reused from NLP Query Generation)
    const [showRefinementModal, setShowRefinementModal] = useState(false);
    const [refinementInstruction, setRefinementInstruction] = useState('');
    const [isRefining, setIsRefining] = useState(false);
    const [refinedQueryData, setRefinedQueryData] = useState(null);
    const [isTestingRefinedQuery, setIsTestingRefinedQuery] = useState(false);
    const [refinedQueryTestResult, setRefinedQueryTestResult] = useState(null);
    const [refinedQueryTestParams, setRefinedQueryTestParams] = useState({});
    const [showRefinedQueryTestResult, setShowRefinedQueryTestResult] = useState('table');
    const [chatTextareaRef, setChatTextareaRef] = useState(null);

    // Main query test state
    const [isTestingQuery, setIsTestingQuery] = useState(false);
    const [queryTestResult, setQueryTestResult] = useState(null);
    const [showQueryTestResult, setShowQueryTestResult] = useState('table');

    // Initialize parameters with default values
    useEffect(() => {
        if (!query && !location.state) {
            // Missing state, navigate back
            navigate(`/agents/queries/${agentId}`);
            return;
        }

        const defaultParams = {};
        if (query?.parameters) {
            query.parameters.forEach(param => {
                defaultParams[param.name] = param.default !== undefined ? param.default : '';
            });
        }
        setParameters(defaultParams);
    }, [query, agentId, navigate, location.state]);

    const fetchExecutionHistory = useCallback(async () => {
        if (!queryId) return;
        setHistoryLoading(true);
        try {
            const response = await getQueryExecutionHistory(queryId, 50);
            if (response.data?.status === 'success') {
                const sortedExecutions = (response.data.data?.executions || []).sort((a, b) => 
                    new Date(b.execution_time) - new Date(a.execution_time)
                );
                setExecutions(sortedExecutions);
                
                // Automatically display the latest execution if it exists and we haven't manually executed yet
                if (sortedExecutions.length > 0 && !executionData) {
                    setExecutionData({
                        execution: sortedExecutions[0],
                        // Note: historical executions might package analysis differently.
                        // Wait, the new parsing logic in ExecutionHistoryModal/QueryExecutionResults will handle parsing.
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch execution history:', error);
            showToast(t('toasts.historyLoadFailed'), 'error');
        } finally {
            setHistoryLoading(false);
        }
    }, [queryId, showToast, executionData, t]);

    useEffect(() => {
        fetchExecutionHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryId]);

    // Auto-focus textarea and reset height when refinement modal opens
    useEffect(() => {
        if (showRefinementModal && chatTextareaRef) {
            chatTextareaRef.focus();
            chatTextareaRef.style.height = 'auto';
        }
    }, [showRefinementModal, chatTextareaRef]);

    const handleParameterChange = (paramName, value) => {
        setParameters(prev => ({
            ...prev,
            [paramName]: value
        }));
        if (errors[paramName]) {
            setErrors(prev => ({
                ...prev,
                [paramName]: null
            }));
        }
    };

    const validateParameters = () => {
        const newErrors = {};
        if (query?.parameters) {
            query.parameters.forEach(param => {
                // Optional params (e.g. filters guarded by NULLIF/'' bypass in SQL) may be blank.
                if (param.required === false) return;
                const value = parameters[param.name];
                if (value === undefined || value === null || value.toString().trim() === '') {
                    newErrors[param.name] = t('validation.fieldRequired', { field: param.name });
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleExecute = async () => {
        if (!validateParameters() || isExecuting) return;

        setIsExecuting(true);
        try {
            const response = await executeQuery(queryId, databaseId, parameters);
            if (response.data?.status === 'success') {
                setExecutionData(response.data.data);
                showToast(t('toasts.executeSuccess'), 'success');
                // Refresh history to include this new execution
                fetchExecutionHistory();
            }
        } catch (error) {
            console.error('Failed to execute query:', error);

            let errMsg = t('toasts.executeFailed');
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                errMsg = detail;
            } else if (Array.isArray(detail)) {
                errMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
            } else if (detail && detail.message) {
                errMsg = detail.message;
            } else if (detail) {
                errMsg = JSON.stringify(detail);
            }
            
            showToast(errMsg, 'error');
        } finally {
            setIsExecuting(false);
        }
    };

    const handleTestQuery = async () => {
        if (!validateParameters() || isTestingQuery) return;

        setIsTestingQuery(true);
        try {
            const response = await testRefinedQuery({
                database_id: databaseId,
                query: query.query,
                parameters: parameters
            });

            setQueryTestResult(response.data);
        } catch (error) {
            console.error('Error testing query:', error);
            setQueryTestResult({ error: error.response?.data?.detail || error.message });
            showToast(t('toasts.testFailed'), 'error');
        } finally {
            setIsTestingQuery(false);
        }
    };

    const handleDeleteSingleExecution = async (executionId) => {
        setConfirmModal({
            isOpen: true,
            executionId: executionId,
            title: t('deleteExecutionTitle'),
            message: t('deleteExecutionMessage'),
        });
    };

    const confirmDeleteExecution = async () => {
        if (!confirmModal.executionId) return;

        try {
            const response = await deleteQueryExecution(confirmModal.executionId);
            if (response.data?.status === 'success') {
                showToast(t('toasts.deleteSuccess'), 'success');
                // Remove from list
                setExecutions(prev => prev.filter(exec => exec.id !== confirmModal.executionId));
                // Clear execution data if this was the selected one
                if (executionData?.execution?.id === confirmModal.executionId || executionData?.query_result?.id === confirmModal.executionId) {
                    setExecutionData(null);
                }
            } else {
                showToast(t('toasts.deleteFailed'), 'error');
            }
        } catch (error) {
            console.error('Failed to delete execution:', error);
            showToast(error?.response?.data?.message || t('toasts.deleteFailed'), 'error');
        }
        
        setConfirmModal(prev => ({ ...prev, isOpen: false, executionId: null }));
    };

    // Query Refinement Functions (reused from NLP Query Generation)
    const openRefinementModal = () => {
        setRefinementInstruction('');
        setRefinedQueryData(null);
        setRefinedQueryTestResult(null);
        setRefinedQueryTestParams({});
        setShowRefinementModal(true);
    };

    const closeRefinementModal = () => {
        setShowRefinementModal(false);
        setRefinementInstruction('');
        setRefinedQueryData(null);
        setRefinedQueryTestResult(null);
        setRefinedQueryTestParams({});
        if (chatTextareaRef) {
            chatTextareaRef.style.height = 'auto';
        }
    };

    const handleRefineQuery = async () => {
        if (!refinementInstruction.trim()) return;

        setIsRefining(true);
        try {
            const response = await refineNLPQuery({
                query_id: queryId,
                instruction: refinementInstruction
            });

            const result = response.data;
            if (result.status === 'success') {
                const refinedParams = result.data.parameters || query.parameters;
                setRefinedQueryData({
                    refined_query: result.data.refined_query,
                    explanation: result.data.explanation,
                    name: result.data.name || query.name,
                    description: result.data.description || query.description,
                    parameters: refinedParams,
                    expected_columns: result.data.expected_columns || query.expected_columns,
                    tables_involved: result.data.tables_involved || query.tables_involved || [],
                    validation_query: result.data.validation_query,
                    validation_status: result.data.validation_status || 'pending'
                });
                // Initialize test params with defaults
                const initialTestParams = {};
                if (refinedParams && refinedParams.length > 0) {
                    refinedParams.forEach(param => {
                        initialTestParams[param.name] = param.default || param.example || '';
                    });
                }
                setRefinedQueryTestParams(initialTestParams);
            } else {
                showToast(result.message || t('toasts.refineFailed'), 'error');
            }
        } catch (error) {
            console.error('Error refining query:', error);
            showToast(error.response?.data?.detail || t('toasts.refineFailed'), 'error');
        } finally {
            setIsRefining(false);
        }
    };

    const handleAcceptRefinement = async () => {
        if (!refinedQueryData) return;

        setIsRefining(true);
        try {
            const response = await updateNLPQuery(queryId, {
                query: refinedQueryData.refined_query,
                name: refinedQueryData.name,
                description: refinedQueryData.description,
                parameters: refinedQueryData.parameters,
                expected_columns: refinedQueryData.expected_columns,
                tables_involved: refinedQueryData.tables_involved
            });

            const result = response.data;
            if (result.status === 'success') {
                closeRefinementModal();
                const reportPlanContext = location.state?.reportPlanContext;
                if (reportPlanContext?.reportGenerationId) {
                    upsertImplementation(
                        reportPlanContext.reportGenerationId,
                        reportPlanContext.recommendationIndex,
                        reportPlanContext.recommendation,
                        {
                            status: IMPLEMENTATION_STATUS.COMPLETED,
                            query_id: Number(queryId),
                            insight_id: reportPlanContext.recommendation?.insight_id,
                        }
                    );
                    showToast(t('toasts.refinedReturningToPlan'), 'success');
                    navigate(
                        buildReportPlanReturnPath(
                            reportPlanContext.databaseId,
                            reportPlanContext.reportGenerationId
                        )
                    );
                    return;
                }
                showToast(t('toasts.refineSaveSuccess'), 'success');
                // Update the query object with refined data
                query.query = refinedQueryData.refined_query;
                query.name = refinedQueryData.name;
                query.description = refinedQueryData.description;
                query.parameters = refinedQueryData.parameters;
            } else {
                showToast(result.message || t('toasts.updateFailed'), 'error');
            }
        } catch (error) {
            console.error('Error updating query:', error);
            showToast(error.response?.data?.detail || t('toasts.updateFailed'), 'error');
        } finally {
            setIsRefining(false);
        }
    };

    const handleTestRefinedQuery = async () => {
        if (!refinedQueryData) return;

        setIsTestingRefinedQuery(true);
        try {
            const response = await testRefinedQuery({
                database_id: databaseId,
                query: refinedQueryData.refined_query,
                parameters: refinedQueryTestParams
            });

            setRefinedQueryTestResult(response.data);
        } catch (error) {
            console.error('Error testing refined query:', error);
            setRefinedQueryTestResult({ error: error.response?.data?.detail || error.message });
            showToast(t('toasts.testFailed'), 'error');
        } finally {
            setIsTestingRefinedQuery(false);
        }
    };

    const autoResizeTextarea = (e) => {
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    };

    const handleRefinementInputChange = (e) => {
        setRefinementInstruction(e.target.value);
        autoResizeTextarea(e);
    };

    if (!query) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-12 h-12 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="page-content">
            {/* Header section */}
            <div className="mb-8">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('executeQuery')}</h1>
                        <p className="text-slate-500 mt-1">
                            {agentName} <span className="mx-2">•</span> {query.name}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Query parameters and execution button */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Execution Box matching Modal */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-8 py-6 pb-2 border-b border-gray-100">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center">
                                    <Play className="w-6 h-6 text-primary-600 stroke-2" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 tracking-tight">{t('heading.queryParameters')}</h3>
                            </div>
                            {query.description && (
                                <p className="text-sm text-slate-500 mt-2">{query.description}</p>
                            )}
                        </div>

                        <div className="p-8">
                            {query.parameters && query.parameters.length > 0 ? (
                                <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                                    {query.parameters.map((param, idx) => (
                                        <div key={idx}>
                                            <label className="block text-sm font-bold text-slate-800 mb-2 lowercase">
                                                {param.name.replace(/_/g, ' ')}
                                                {param.description && (
                                                    <span className="block text-xs text-gray-400 mt-1 font-normal normal-case">
                                                        {param.description}
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                                                value={parameters[param.name] || ''}
                                                onChange={(e) => handleParameterChange(param.name, e.target.value)}
                                                className={`w-full px-4 py-3 rounded-xl border border-gray-200 bg-white placeholder-gray-400 text-slate-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm ${
                                                    errors[param.name] 
                                                        ? 'border-red-300 ring-2 ring-red-100' 
                                                        : ''
                                                }`}
                                                placeholder={param.default || t('placeholders.enterField', { field: param.name })}
                                            />
                                            {errors[param.name] && (
                                                <div className="flex items-center mt-2 text-sm text-red-600">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors[param.name]}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <p className="text-sm text-gray-600">
                                        {t('messages.noParametersRequired')}
                                    </p>
                                </div>
                            )}

                            <div className="flex justify-end mt-8 gap-3">
                                <button
                                    onClick={handleTestQuery}
                                    disabled={isTestingQuery}
                                    className={`px-6 py-2.5 text-primary-700 bg-primary-100 rounded-xl font-semibold flex items-center justify-center gap-3 shadow-sm transition-colors ${
                                        isTestingQuery
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:bg-primary-200'
                                    }`}
                                >
                                    {isTestingQuery ? (
                                        <>
                                            <Loader className="w-5 h-5 animate-spin" />
                                            <span>{t('buttons.testing')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Play className="w-5 h-5" />
                                            <span>{t('buttons.testQuery')}</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleExecute}
                                    disabled={isExecuting}
                                    className={`px-6 py-2.5 text-white rounded-xl font-semibold flex items-center justify-center gap-3 shadow-md transition-colors ${
                                        isExecuting
                                            ? 'bg-primary-400 cursor-not-allowed'
                                            : 'bg-primary-600 hover:bg-primary-700'
                                    }`}
                                >
                                    {isExecuting ? (
                                        <>
                                            <Loader className="w-5 h-5 animate-spin" />
                                            <span>{t('buttons.executing')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Play className="w-5 h-5 fill-current opacity-80" />
                                            <span>{t('executeQuery')}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Test Results Section */}
                        {queryTestResult && (
                            <div className="p-8 border-t border-gray-100 bg-gray-50/30">
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="font-semibold text-gray-900 text-lg">{t('heading.testResults')}</h4>
                                    <select
                                        value={showQueryTestResult}
                                        onChange={(e) => setShowQueryTestResult(e.target.value)}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                                    >
                                        <option value="table">{t('options.tableView')}</option>
                                        <option value="json">{t('options.jsonView')}</option>
                                    </select>
                                </div>

                                {queryTestResult.error ? (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-center gap-2 text-red-700">
                                            <AlertCircle className="w-5 h-5" />
                                            <p className="text-sm font-medium">{t('messages.testFailed')}</p>
                                        </div>
                                        <p className="text-sm text-red-600 mt-2">
                                            {typeof queryTestResult.error === 'string' 
                                                ? queryTestResult.error 
                                                : JSON.stringify(queryTestResult.error)}
                                        </p>
                                    </div>
                                ) : showQueryTestResult === 'table' ? (
                                    <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-200 bg-gray-50/50">
                                                    {queryTestResult.data?.rows && queryTestResult.data.rows.length > 0 && (
                                                        Object.keys(queryTestResult.data.rows[0]).map((key, idx) => (
                                                            <th key={idx} className="text-left py-3 px-4 font-semibold text-gray-700">
                                                                {key.replace(/_/g, ' ')}
                                                            </th>
                                                        ))
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {queryTestResult.data?.rows && queryTestResult.data.rows.map((row, rowIdx) => (
                                                    <tr key={rowIdx} className="border-b border-gray-100 hover:bg-gray-50">
                                                        {Object.values(row).map((value, colIdx) => (
                                                            <td key={colIdx} className="py-3 px-4 text-gray-600">
                                                                {typeof value === 'number' ? value.toLocaleString() : value}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {queryTestResult.data?.rows && queryTestResult.data.rows.length > 0 && (
                                            <div className="px-4 py-3 border-t border-gray-100">
                                                <p className="text-sm text-gray-500 font-medium">
                                                    {t('messages.showingRows', { count: queryTestResult.data.rows.length })}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <pre className="bg-white border border-gray-200 rounded-xl p-4 overflow-x-auto text-sm text-gray-700 shadow-sm">
                                        {JSON.stringify(queryTestResult.data, null, 2)}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Displays Query Text */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                            <h4 className="font-semibold text-gray-700 uppercase tracking-wide text-sm">{t('heading.querySyntax')}</h4>
                        </div>
                        <div className="p-6">
                            <PlainQueryDisplay sql={query.query} />
                            
                            <div className="flex justify-end mt-6 pt-6 border-t border-gray-100">
                                <button
                                    onClick={openRefinementModal}
                                    className="px-5 py-2.5 text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-xl font-semibold flex items-center gap-2 shadow-sm transition-colors text-sm"
                                    title={t('tooltips.refineQueryAI')}
                                >
                                    <Sparkles className="w-4 h-4" />
                                    <span>{t('refineQuery')}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Latest Result / execution Data Component embedding */}
                    {executionData && (
                        <div className="mt-8 border-t border-slate-200 pt-8">
                            <h3 className="text-2xl font-bold text-slate-900 tracking-tight mb-6">{t('heading.results')}</h3>
                            {/* We inline the QueryExecutionResults by overriding its modal styling using a wrapper or we can mount it directly */}
                            {/* The QueryExecutionResults component is naturally a modal, but we can reuse its dashboard internal wrapper. We will do this carefully below: */}
                            <div className="relative border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                                {/* Using absolute positioning cheat or passing a prop. Since it's a modal (fixed), we might need to recreate the layout here or pass a prop. Wait, we can render the new dashboard directly visually. */}
                                {/* Since the user edited QueryExecutionResults.jsx directly in `frontend/...`, it might still be `fixed inset-0`. I'll render a modified version here if it breaks, but for now we mount it inline. Actually, let's look at `QueryExecutionResults`. It has `<div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50/80 backdrop-blur-sm">`. That will take over the screen. So we shouldn't use `QueryExecutionResults`. Instead, we should just let them see the results array, OR recreate the view natively. */}
                                {/* Wait, the prompt says "pervious results below it... once i execute i should get the latest result...". We can inline the data. */}
                                <ResultDashboard executionData={executionData} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column: Execution History List */}
                <div className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                <History className="w-5 h-5 text-gray-500" />
                                {t('heading.executionHistory')}
                            </h3>
                            <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-1 rounded-full">{executions.length}</span>
                        </div>
                        <div className="max-h-[800px] overflow-y-auto p-4 space-y-3">
                            {historyLoading ? (
                                <div className="flex justify-center py-8">
                                    <Loader className="w-8 h-8 animate-spin text-gray-400" />
                                </div>
                            ) : executions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 text-sm">
                                    {t('messages.noPreviousExecutions')}
                                </div>
                            ) : (
                                executions.map((exec, idx) => (
                                    <div
                                        key={exec.id}
                                        className={`w-full p-4 rounded-xl border transition-all ${
                                            executionData?.execution?.id === exec.id
                                                ? 'bg-primary-50 border-primary-200 shadow-sm'
                                                : 'bg-white border-gray-100 hover:border-primary-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div
                                            onClick={() => setExecutionData({ execution: exec })}
                                            className="flex items-center justify-between mb-2 cursor-pointer"
                                        >
                                            <span className="text-sm font-semibold text-slate-800">{t('labels.executionNumber', { number: executions.length - idx })}</span>
                                            <div className="flex items-center gap-2">
                                                {exec.status === 'success' ? (
                                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 text-rose-500" />
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSingleExecution(exec.id);
                                                    }}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title={t('tooltips.deleteExecution')}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            onClick={() => setExecutionData({ execution: exec })}
                                            className="cursor-pointer"
                                        >
                                            <div className="flex flex-wrap text-xs text-slate-500 gap-y-1">
                                                <div className="w-full">{new Date(exec.execution_time).toLocaleString()}</div>
                                                <div className="mr-3">{exec.execution_duration_ms || 0}ms</div>
                                                <div>{exec.row_count || 0} rows</div>
                                            </div>
                                            {/* Display parameters used */}
                                            {(() => {
                                                const params = exec.parameters_used && Object.keys(exec.parameters_used).length > 0
                                                    ? exec.parameters_used
                                                    : (exec.master_agent_analysis?.query_parameters_used || {});
                                                return Object.keys(params).length > 0 ? (
                                                    <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-slate-500">
                                                        {Object.entries(params).map(([key, value]) => (
                                                            <div key={key}>
                                                                <span className="font-medium text-slate-600">{key}:</span> {String(value)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmDeleteExecution}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={t('common:delete')}
                cancelText={t('common:cancel')}
                variant="danger"
            />

            {/* Query Refinement Modal (reused from NLP Query Generation) */}
            {showRefinementModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg z-10">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-primary-600" />
                                <h3 className="text-lg font-semibold text-gray-900">{t('refineQuery')}</h3>
                            </div>
                            <button
                                onClick={closeRefinementModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-4">
                            {/* Original Query Info */}
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <h4 className="font-semibold text-gray-900 text-sm mb-2">{t('heading.originalQuery')}</h4>
                                <p className="text-xs text-gray-600 mb-2">{query.name}</p>
                                <PlainQueryDisplay sql={query.query} />
                            </div>

                            {/* Refinement Instruction */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {t('labels.refinementInstruction')}
                                </label>
                                <div className="relative">
                                    <textarea
                                        ref={setChatTextareaRef}
                                        value={refinementInstruction}
                                        onChange={handleRefinementInputChange}
                                        placeholder={t('placeholders.refinementInstruction')}
                                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                                        rows="3"
                                        style={{ minHeight: '80px', maxHeight: '200px' }}
                                    />
                                    <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                                        {t('hints.naturalLanguageHint')}
                                    </div>
                                </div>
                            </div>

                            {/* Refine Button */}
                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={handleRefineQuery}
                                    disabled={isRefining || !refinementInstruction.trim()}
                                    className={`px-6 py-2.5 text-white rounded-lg font-semibold flex items-center gap-2 shadow-md transition-colors ${
                                        isRefining || !refinementInstruction.trim()
                                            ? 'bg-primary-400 cursor-not-allowed'
                                            : 'bg-primary-600 hover:bg-primary-700'
                                    }`}
                                >
                                    {isRefining ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            <span>{t('buttons.refining')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            <span>{t('refineQuery')}</span>
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Refined Query Result */}
                            {refinedQueryData && (
                                <div className="border-t border-gray-200 pt-4 space-y-4">
                                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                        <div className="flex items-center gap-2 mb-2">
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                            <h4 className="font-semibold text-green-900 text-sm">Refined Query</h4>
                                        </div>
                                        <p className="text-xs text-green-700 mb-2">{refinedQueryData.explanation}</p>
                                        <PlainQueryDisplay sql={refinedQueryData.refined_query} />
                                    </div>

                                    {/* Parameter Input Fields */}
                                    {refinedQueryData.parameters && refinedQueryData.parameters.length > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <h4 className="font-semibold text-gray-900 text-sm mb-3">Test Parameters</h4>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                {refinedQueryData.parameters.map((param, idx) => (
                                                    <div key={idx}>
                                                        <label className="block text-sm font-bold text-slate-800 mb-2 lowercase">
                                                            {param.name.replace(/_/g, ' ')}
                                                            {param.description && (
                                                                <span className="block text-xs text-gray-400 mt-1 font-normal normal-case">
                                                                    {param.description}
                                                                </span>
                                                            )}
                                                        </label>
                                                        <input
                                                            type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                                                            value={refinedQueryTestParams[param.name] || ''}
                                                            onChange={(e) => setRefinedQueryTestParams(prev => ({
                                                                ...prev,
                                                                [param.name]: e.target.value
                                                            }))}
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                            placeholder={param.default || param.example || `Enter ${param.name}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Test Button */}
                                    <div className="flex justify-end pt-2">
                                        <button
                                            onClick={handleTestRefinedQuery}
                                            disabled={isTestingRefinedQuery}
                                            className={`px-6 py-2.5 text-white rounded-lg font-semibold flex items-center gap-2 shadow-md transition-colors ${
                                                isTestingRefinedQuery
                                                    ? 'bg-primary-400 cursor-not-allowed'
                                                    : 'bg-primary-600 hover:bg-primary-700'
                                            }`}
                                        >
                                            {isTestingRefinedQuery ? (
                                                <>
                                                    <Loader className="w-4 h-4 animate-spin" />
                                                    <span>Testing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-4 h-4" />
                                                    <span>Test Query</span>
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Test Results */}
                                    {refinedQueryTestResult && (
                                        <div className="border-t border-gray-200 pt-4 space-y-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-semibold text-gray-900 text-sm">Test Results</h4>
                                                <select
                                                    value={showRefinedQueryTestResult}
                                                    onChange={(e) => setShowRefinedQueryTestResult(e.target.value)}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="table">Table View</option>
                                                    <option value="json">JSON View</option>
                                                </select>
                                            </div>

                                            {refinedQueryTestResult.error ? (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                                    <div className="flex items-center gap-2 text-red-700">
                                                        <AlertCircle className="w-5 h-5" />
                                                        <p className="text-sm font-medium">Test Failed</p>
                                                    </div>
                                                    <p className="text-xs text-red-600 mt-2">
                                                        {typeof refinedQueryTestResult.error === 'string' 
                                                            ? refinedQueryTestResult.error 
                                                            : JSON.stringify(refinedQueryTestResult.error)}
                                                    </p>
                                                </div>
                                            ) : showRefinedQueryTestResult === 'table' ? (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full text-sm">
                                                        <thead>
                                                            <tr className="border-b border-gray-200">
                                                                {refinedQueryTestResult.data?.rows && refinedQueryTestResult.data.rows.length > 0 && (
                                                                    Object.keys(refinedQueryTestResult.data.rows[0]).map((key, idx) => (
                                                                        <th key={idx} className="text-left py-2 px-3 font-medium text-gray-700">
                                                                            {key.replace(/_/g, ' ')}
                                                                        </th>
                                                                    ))
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {refinedQueryTestResult.data?.rows && refinedQueryTestResult.data.rows.map((row, rowIdx) => (
                                                                <tr key={rowIdx} className="border-b border-gray-100 hover:bg-gray-50">
                                                                    {Object.values(row).map((value, colIdx) => (
                                                                        <td key={colIdx} className="py-2 px-3 text-gray-600">
                                                                            {typeof value === 'number' ? value.toLocaleString() : value}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {refinedQueryTestResult.data?.rows && refinedQueryTestResult.data.rows.length > 0 && (
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            Showing {refinedQueryTestResult.data.rows.length} rows
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto text-xs text-gray-700">
                                                    {JSON.stringify(refinedQueryTestResult.data, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    )}

                                    {/* Accept/Reject Buttons */}
                                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={closeRefinementModal}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                        >
                                            Reject
                                        </button>
                                        <button
                                            onClick={handleAcceptRefinement}
                                            disabled={isRefining}
                                            className={`px-6 py-2 text-white rounded-lg font-semibold flex items-center gap-2 shadow-md transition-colors ${
                                                isRefining
                                                    ? 'bg-green-400 cursor-not-allowed'
                                                    : 'bg-green-600 hover:bg-green-700'
                                            }`}
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            <span>Accept & Save</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Extracted and flattened dashboard component
const ResultDashboard = ({ executionData }) => {
    // Determine whether the data comes from history (executionData.execution) or fresh executeQuery (executionData.query_result & executionData.analysis)
    const execution = executionData.execution || executionData.query_result || {};
    const analysisInfo = executionData.analysis || execution.master_agent_analysis || execution.analysis || {};

    let columns = [];
    let data = [];
    
    // First try to get columns and data directly if provided in the execution object (history) or query_result (fresh)
    if (execution.columns && Array.isArray(execution.columns)) {
        columns = execution.columns;
    }
    if (execution.data && Array.isArray(execution.data)) {
        data = execution.data;
    }

    // Try parsing from result_preview string if data is still empty
    if ((!data || data.length === 0) && execution.result_preview) {
        try {
            if (typeof execution.result_preview === 'string') {
                const parsed = JSON.parse(execution.result_preview);
                columns = columns.length ? columns : (parsed.columns || []);
                data = parsed.rows || parsed.data || [];
            } else {
                columns = columns.length ? columns : (execution.result_preview.columns || []);
                data = execution.result_preview.rows || execution.result_preview.data || [];
            }
        } catch(e) {
            // Ignore parse errors
        }
    }

    // Try query_result_snapshot from analysis if still empty
    if ((!data || data.length === 0) && analysisInfo?.query_result_snapshot) {
        try {
            const snapshot = typeof analysisInfo.query_result_snapshot === 'string' 
                ? JSON.parse(analysisInfo.query_result_snapshot)
                : analysisInfo.query_result_snapshot;
            columns = columns.length ? columns : (snapshot.columns || []);
            data = snapshot.rows || snapshot.data || [];
        } catch (e) {
            // Ignore parse errors
        }
    }

    // Execution could use 'status' (history) or 'execution_status' (fresh API)
    const rawStatus = execution.status || execution.execution_status || 'Unknown';
    const isSuccess = rawStatus.toLowerCase() === 'success';

    let master_agent_analysis = {};
    if (typeof analysisInfo === 'string') {
        try {
            master_agent_analysis = JSON.parse(analysisInfo);
        } catch(e) {}
    } else {
        master_agent_analysis = analysisInfo;
    }

    // Extract all analysis fields (backend now strips markdown code blocks)
    const summaryText = (master_agent_analysis?.summary || master_agent_analysis?.executive_summary || master_agent_analysis?.analysis_summary || '').trim();
    const keyFindings = master_agent_analysis?.key_findings || master_agent_analysis?.keyFindings || master_agent_analysis?.key_findings_snapshot || [];
    const actionableInsights = master_agent_analysis?.actionable_insights || master_agent_analysis?.actionableInsights || [];
    const detailedInterpretation = master_agent_analysis?.detailed_interpretation || master_agent_analysis?.detailedInterpretation || '';
    const dotColors = ['bg-emerald-500', 'bg-sky-500', 'bg-amber-400', 'bg-rose-400', 'bg-primary-500'];

    return (
        <div className="bg-white p-8">
            {/* Run Metadata Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
                <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl">
                    <p className="text-xs text-slate-500 font-medium tracking-wide mb-1">Status</p>
                    <div className="flex items-center gap-2 mt-2">
                        {isSuccess ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-rose-500" />
                        )}
                        <p className={`text-sm font-bold ${isSuccess ? 'text-slate-900' : 'text-rose-600'}`}>
                            {rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)}
                        </p>
                    </div>
                </div>
                <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl">
                    <p className="text-xs text-slate-500 font-medium tracking-wide mb-1">Execution Time</p>
                    <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-5 h-5 text-primary-500" />
                        <p className="text-sm font-bold text-slate-900">{execution.execution_duration_ms || execution.execution_time_ms || 0} ms</p>
                    </div>
                </div>
                <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl">
                    <p className="text-xs text-slate-500 font-medium tracking-wide mb-1">Rows</p>
                    <div className="flex items-center gap-2 mt-2">
                        <Database className="w-5 h-5 text-primary-500" />
                        <p className="text-sm font-bold text-slate-900">{execution.row_count || 0}</p>
                    </div>
                </div>
                <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl">
                    <p className="text-xs text-slate-500 font-medium tracking-wide mb-1">Execution ID</p>
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm font-bold text-slate-900">#{execution.id || 'Current'}</span>
                    </div>
                </div>
            </div>

            {/* AI Insight Summary Card */}
            {summaryText && (
                <div className="bg-primary-50/30 border border-primary-100 rounded-2xl p-6 mb-10 relative overflow-hidden">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-primary-600" />
                        <h3 className="font-bold text-primary-900 text-lg">AI Insight Summary</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-700 max-w-4xl relative z-10">
                        {summaryText}
                    </p>
                </div>
            )}

            {/* Key Findings Bullet Grid */}
            {keyFindings && keyFindings.length > 0 && (
                <div className="py-8 border-y border-slate-100 mb-10">
                    <h4 className="font-bold text-slate-800 mb-6 text-lg">Key Findings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
                        {keyFindings.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${dotColors[idx % dotColors.length]}`}></div>
                                <p className="text-sm text-slate-700 font-medium leading-relaxed">
                                    {typeof item === 'object' ? item.finding : item}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Actionable Insights */}
            {actionableInsights && actionableInsights.length > 0 && (
                <div className="mb-10">
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        <h4 className="font-bold text-slate-800 text-lg">Actionable Insights</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        {actionableInsights.map((item, idx) => (
                            <div key={idx} className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-white text-xs font-bold">{idx + 1}</span>
                                    </div>
                                    <p className="text-sm text-slate-700 font-medium leading-relaxed">
                                        {typeof item === 'object' ? item.insight : item}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Detailed Interpretation */}
            {detailedInterpretation && (
                <div className="mb-10">
                    <h4 className="font-bold text-slate-800 mb-4 text-lg">Detailed Interpretation</h4>
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                        <p className="text-sm text-slate-700 leading-relaxed">
                            {detailedInterpretation}
                        </p>
                    </div>
                </div>
            )}

            {/* Data Results Table */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-8">
                <div className="px-6 py-4 bg-slate-50/80 border-b border-slate-200 flex items-center gap-2">
                    <Table className="w-4 h-4 text-slate-500" />
                    <h4 className="font-bold text-slate-800 text-sm">Result Preview</h4>
                </div>
                
                {!columns || columns.length === 0 ? (
                    <div className="text-center py-16 text-slate-400">
                        <Database className="w-10 h-10 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">No tabular data returned for this execution.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                                <tr>
                                    {columns.map((column, idx) => (
                                        <th 
                                            key={idx}
                                            className="px-6 py-3 text-left text-xxs font-bold text-slate-400 tracking-wider uppercase whitespace-nowrap"
                                        >
                                            {column}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                                {data && data.length > 0 ? (
                                    data.slice(0, 100).map((row, rowIdx) => (
                                        <tr key={rowIdx} className="hover:bg-slate-50/50 transition-colors">
                                            {columns.map((col, colIdx) => (
                                                <td 
                                                    key={colIdx}
                                                    className="px-6 py-3.5 text-sm text-slate-600 font-medium whitespace-nowrap"
                                                >
                                                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : (
                                                        <span className="text-slate-300 italic">NULL</span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td 
                                            colSpan={columns.length}
                                            className="px-6 py-8 text-center text-slate-400 text-sm"
                                        >
                                            No rows returned.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {data && data.length > 100 && (
                            <div className="bg-slate-50 px-6 py-4 text-sm text-slate-500 text-center border-t border-slate-100 font-medium">
                                Showing 100 of {data.length} rows.
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Error Details */}
            {execution.error_details && (
                <div className="mt-8 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <AlertCircle className="w-5 h-5 text-rose-600" />
                        <h4 className="font-bold text-rose-800 text-lg">Error Details</h4>
                    </div>
                    <div className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
                        <pre className="text-sm text-rose-700 whitespace-pre-wrap font-mono">
                            {typeof execution.error_details === 'string' 
                                ? execution.error_details 
                                : JSON.stringify(execution.error_details)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};
